import {
  PanelItem,
  PanelLogo,
  SidebarWrapper,
  PanelLogoWrapper,
  BlurredOverlay,
} from "../../pageComponents/auth/style";
import {
  DashboardIcon,
  DiscoveryIcon,
  LockFolder,
  LogoSmall,
  OpenFolder,
  SwipeFileIcon,
  TeamFolder,
} from "../../assets/Icons";
import { Box, Button, Typography, Tooltip, Divider } from "@mui/material";
import Logo from "../../assets/images/Logo.png";
import useConfig from "../../hooks/useConfig";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import SidebarFolder from "../sidebarFolder";
import routeConstant from "../../utils/routeConstant";
import ShareAccessToTeamAndUsersModal from "../shareAccessToTeamAndUsersModal";
import SidebarSharedFolders from "../sidebarSharedFolder";
import { userDetailsSelector } from "../../store/account/selector";
import useAuth from "../../hooks/useAuth";
import { MetaDataReducerAction } from "../../store/metadata/slice";
import SidebarTeamsFolders from "../SidebarTeamsFolders";
import { DiscoveryReducerAction } from "../../store/discovery/slice";
import { selectedTabIndexSelector } from "../../store/metadata/selector";

const drawerData = [
  {
    name: "Dashboard",
    icon: <DashboardIcon />,
    link: routeConstant.dashboard,
  },
  {
    name: "Swiped Ads",
    icon: <SwipeFileIcon />,
    link: routeConstant.swipeFiles,
  },
  {
    name: "Explore Lounge",
    icon: <DiscoveryIcon />,
    link: routeConstant.discovery,
  },
];

const Sidebar = () => {
  const { drawerOpen, onChangeDrawer } = useConfig();
  const { pathname } = useLocation();
  const { folderId } = useParams();
  const navigate = useNavigate();
  const userDetails = useSelector(userDetailsSelector);
  const CLICKED_URL = "/" + pathname.split("/")[1];
  const dispatch = useDispatch();
  const selectedTabIndex = useSelector(selectedTabIndexSelector);
  const { isAuthenticated, isPathNameFolder } = useAuth();

  useEffect(() => {
    if (userDetails?.planId || userDetails?.isInvitedBySeat === "ACCEPTED") {
      dispatch(MetaDataReducerAction.setFilterOptionSkipValue(0));
      dispatch(DiscoveryReducerAction.setFilterOptionSkipValue(0));
      // dispatch(DiscoveryReducerAction.resetDiscoveryState())
      dispatch(MetaDataReducerAction.resetSwipefileState());
      dispatch(MetaDataSagaActions.fetchSharedFolderList());
      dispatch(
        MetaDataSagaActions.fetchFolderList({
          selectedFolderId: folderId,
          selectedTabIndex,
        })
      );
    }
  }, [userDetails]);

  const handleClick = (item: { name: string; link: string }) => {
    dispatch(DiscoveryReducerAction.setDiscoveryTabIndex(0));
    navigate(item.link);
  };

  const handleBlurSectionClick = () => {
    if (!isAuthenticated) {
      dispatch(MetaDataReducerAction.setOpenRegisterNowModal(true));
    }
  };

  // Component to wrap PanelItem with conditional tooltip
  const TooltipWrapper = ({
    children,
    title,
    disabled = false,
  }: {
    children: React.ReactElement;
    title: string;
    disabled?: boolean;
  }) => {
    if (disabled || drawerOpen) {
      return children;
    }

    return (
      <Tooltip
        title={title}
        placement="right"
        arrow
        enterDelay={300}
        leaveDelay={200}
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: "rgba(33, 33, 33, 0.9)",
              color: "white",
              fontSize: "0.875rem",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "8px 12px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              "& .MuiTooltip-arrow": {
                color: "rgba(33, 33, 33, 0.9)",
              },
            },
          },
        }}
      >
        {children}
      </Tooltip>
    );
  };

  return (
    <SidebarWrapper id="side-panel" drawerOpen={drawerOpen}>
      <Tooltip
        title={drawerOpen ? "" : "Swipe Lounge"}
        placement="right"
        arrow
        enterDelay={300}
        leaveDelay={200}
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: "rgba(33, 33, 33, 0.9)",
              color: "white",
              fontSize: "0.875rem",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "8px 12px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              "& .MuiTooltip-arrow": {
                color: "rgba(33, 33, 33, 0.9)",
              },
            },
          },
        }}
      >
        <Button sx={{ m: 0, p: 0 }} onClick={() => navigate("/")}>
          {drawerOpen ? (
            <PanelLogo src={Logo} alt="brand-logo" />
          ) : (
            <PanelLogoWrapper>
              <LogoSmall />
            </PanelLogoWrapper>
          )}
        </Button>
      </Tooltip>

      <Box
        sx={{ position: "relative", display: "flex", flexDirection: "column" }}
      >
        {!isAuthenticated && (
          <BlurredOverlay onClick={handleBlurSectionClick} />
        )}
        {drawerData.map((item, index: number) => (
          <TooltipWrapper
            key={index}
            title={item.name}
            disabled={!isAuthenticated}
          >
            <PanelItem
              to={item?.link}
              onClick={
                item?.name === "Explore Lounge"
                  ? () => handleClick(item)
                  : undefined
              }
              drawerOpen={drawerOpen}
              style={{
                background:
                  item?.link === CLICKED_URL
                    ? "linear-gradient(94.04deg, #1c75bc 2.44%, #00aeef 112.73%)"
                    : "transparent",
              }}
            >
              {item?.icon}
              {item?.name && drawerOpen && (
                <Typography style={{ marginLeft: "0.857rem" }}>
                  {item?.name}
                </Typography>
              )}
            </PanelItem>
          </TooltipWrapper>
        ))}
      </Box>

      {drawerOpen ? (
        <>
          <SidebarFolder />
          <Box sx={{ position: "relative" }}>
            {!isAuthenticated && (
              <BlurredOverlay onClick={handleBlurSectionClick} />
            )}
            <SidebarTeamsFolders />
            <SidebarSharedFolders />
          </Box>
        </>
      ) : (
        <>
          {/* Folders Section */}
          <Divider sx={{ borderColor: "#2f4757" }} />
          <TooltipWrapper title="My Folders">
            <PanelItem
              onClick={() => {
                if (!drawerOpen) {
                  onChangeDrawer(true); // expand sidebar
                  setTimeout(() => {
                    navigate({ pathname: routeConstant.starred });
                  }, 200); // wait for expand animation
                } else {
                  navigate({ pathname: routeConstant.starred });
                }
              }}
              drawerOpen={drawerOpen}
            >
              <LockFolder />
            </PanelItem>
          </TooltipWrapper>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {!isAuthenticated && (
              <BlurredOverlay onClick={handleBlurSectionClick} />
            )}

            <Divider sx={{ borderColor: "#2f4757" }} />
            <TooltipWrapper title="Team Folders">
              <PanelItem
                onClick={() => {
                  if (!drawerOpen) {
                    onChangeDrawer(!drawerOpen);
                  }
                }}
                drawerOpen={drawerOpen}
              >
                <TeamFolder />
              </PanelItem>
            </TooltipWrapper>

            <Divider sx={{ borderColor: "#2f4757" }} />
            <TooltipWrapper title="Shared Folders">
              <PanelItem
                onClick={() => {
                  if (!drawerOpen) {
                    onChangeDrawer(!drawerOpen);
                  }
                }}
                drawerOpen={drawerOpen}
              >
                <OpenFolder />
              </PanelItem>
            </TooltipWrapper>
          </Box>
          {/* When expanded, render full folder components */}
          {drawerOpen && (
            <>
              <SidebarFolder />
              <Box sx={{ position: "relative" }}>
                {!isAuthenticated && (
                  <BlurredOverlay onClick={handleBlurSectionClick} />
                )}
                <SidebarTeamsFolders />
                <SidebarSharedFolders />
              </Box>
            </>
          )}
        </>
      )}

      {isAuthenticated ? <ShareAccessToTeamAndUsersModal /> : null}
    </SidebarWrapper>
  );
};

export default Sidebar;
